import React from "react";
import {
  Alert,
  Card,
  CardStackSection,
  Divider,
  Stack,
  Text,
  TextLinkStyles,
} from "@origin-digital/ods-core";
import { chatMessengerShow, navTo } from "@origin-digital/event-dispatcher";
import { Description, DescriptionList } from "../../components/DescriptionList";
import { formatLandline, formatMobile, formatPhone } from "../../utils/helpers";
import { Password } from "../../components/Password/Password";
import { AddressProp } from "../UpdateAddress";
import {
  ProfileFieldMap,
  RefetchFieldFunction,
} from "../../crm/profileFieldMap";
import { BusinessDetail } from "./BusinessDetail";
import { PersonalDetail } from "./PersonalDetails";
import content from "./content.json";

interface ManageScreenProps {
  fieldMap: ProfileFieldMap;
  refetch: RefetchFieldFunction;
  hasLPG: boolean;
  hasLPGOnly: boolean;
}

const getUniqueAddresses = (addressProps: AddressProp[]) =>
  addressProps.reduce((addresses, { address }) => {
    // ignore casing
    const transformedAddress = address?.toUpperCase();

    if (
      address &&
      !addresses.some(
        (existingItem) =>
          // ignore the commas
          existingItem.replace(/,/g, "") ===
          transformedAddress.replace(/,/g, "")
      )
    ) {
      addresses.push(transformedAddress);
    }
    return addresses;
  }, [] as string[]) ?? [];

export const getLinkText = (value?: string | null) =>
  content.links[value ? "change" : "add"];

export const ManageScreen = ({
  fieldMap,
  refetch,
  hasLPG,
  hasLPGOnly,
}: ManageScreenProps) => {
  const handleMessageUsClick = (e: any) => {
    e.preventDefault();
    chatMessengerShow({});
  };

  const callNavTo =
    (navToEvent: () => Promise<Record<string, unknown>>) => async () => {
      // if someone cancels out of the navTo flow then it rejects the promise
      // so we need to catch it but we don't need to actually do anything
      try {
        await navToEvent();
        refetch();
      } catch (_) {}
    };

  const email = fieldMap.EMAIL;
  const mobile = fieldMap.MOBILE;
  const landline = fieldMap.LANDLINE;
  const homephone = fieldMap.HOME_PHONE;
  const billingAddress = fieldMap.BILLING_ADDRESS;
  const addresses = getUniqueAddresses(billingAddress?.data || []);

  return (
    <CardStackSection data-id="manage-profile">
      <Card>
        <Stack space={["xlarge", "xxlarge"]}>
          {hasLPG && !hasLPGOnly && (
            <Alert data-id="lpg-alert-box" title="LPG customers">
              <Stack space="medium">
                <Text>
                  {content.hasLPG.body[0]}{" "}
                  <TextLinkStyles
                    as="button"
                    data-id="update-contact-details-lpg"
                    onClick={handleMessageUsClick}
                  >
                    message us
                  </TextLinkStyles>{" "}
                  {content.hasLPG.body[1]}
                </Text>
              </Stack>
            </Alert>
          )}
          {fieldMap.BUSINESS_ACCOUNT && (
            <BusinessDetail businessDetails={fieldMap.BUSINESS_ACCOUNT.data} />
          )}
          <Stack>
            <PersonalDetail fieldMap={fieldMap} callNavTo={callNavTo} />
            <DescriptionList
              data-id="contact-details"
              heading={content.contact.heading}
            >
              {email && (
                <Description
                  data-id="email"
                  label={content.contact.email}
                  value={email.data || undefined}
                  linkText={
                    email.editable && email.useUpdate
                      ? content.links.change
                      : undefined
                  }
                  onClick={callNavTo(navTo.profileEmail)}
                />
              )}
              {email && <Divider />}
              {mobile && (
                <Description
                  data-id="mobile"
                  label={content.contact.mobile}
                  linkText={
                    mobile.editable && mobile.useUpdate
                      ? getLinkText(mobile.data)
                      : undefined
                  }
                  onClick={callNavTo(navTo.profileMobile)}
                  value={mobile.data ? formatMobile(mobile.data) : ""}
                />
              )}
              {mobile && <Divider />}
              {landline && (
                <Description
                  data-id="landline"
                  label={content.contact.landline}
                  linkText={
                    landline.editable && landline.useUpdate
                      ? getLinkText(landline.data)
                      : undefined
                  }
                  onClick={callNavTo(navTo.profileLandline)}
                  value={landline.data ? formatLandline(landline.data) : ""}
                />
              )}
              {landline && <Divider />}
              {homephone && (
                <Description
                  data-id="homephone"
                  label={content.contact.homephone}
                  linkText={
                    homephone.editable && homephone.useUpdate
                      ? getLinkText(homephone.data)
                      : undefined
                  }
                  onClick={callNavTo(navTo.profileLandline)}
                  value={homephone.data ? formatPhone(homephone.data) : ""}
                />
              )}
              {homephone && <Divider />}
              {billingAddress && (
                <Description
                  data-id="address"
                  label={
                    content.contact[
                      addresses.length > 1 ? "addressMultiple" : "address"
                    ]
                  }
                  linkText={
                    billingAddress.editable && billingAddress.useUpdate
                      ? getLinkText(addresses[0])
                      : undefined
                  }
                  onClick={callNavTo(navTo.profileAddress)}
                  value={addresses.map((address: string) =>
                    address.toUpperCase()
                  )}
                />
              )}
              {billingAddress && <Divider />}
            </DescriptionList>
            <Password />
          </Stack>
        </Stack>
      </Card>
    </CardStackSection>
  );
};
